import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck, Zap, Headset, GraduationCap, Database, AppWindow, Package, Video, Users, Radio, CalendarCheck, FileDown } from "lucide-react";
import { api } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { AdSlots } from "../components/AdSlots";
import { MaskedLine, Reveal, EASE } from "../components/Motion";
import { PlaquetteEmailForm } from "../components/PlaquetteEmailForm";
import { PresentationVideo } from "../components/offres/GalerieVideos";

const HERO_IMG = "https://images.unsplash.com/photo-1678695972687-033fa0bdbac9";

const CATS = [
  { name: "Formations médicales", icon: GraduationCap, img: "https://images.unsplash.com/photo-1758691462848-31a39258dbd8", desc: "Vidéos en streaming et guides PDF" },
  { name: "Outils de gestion de l'information médicale", icon: Database, img: "https://images.unsplash.com/photo-1551288049-bebda4e38f71", desc: "Registres, tableurs et modèles SIH" },
  { name: "Applications informatiques", icon: AppWindow, img: "https://images.unsplash.com/photo-1542831371-29b0f74f9713", desc: "Licences logicielles dégressives" },
  { name: "Autres produits digitaux", icon: Package, img: "https://images.unsplash.com/photo-1666886573264-38075cc56104", desc: "Icônes, templates et ressources" },
];

const RDV_TYPES = [
  { key: "teleconsultation", icon: Video, label: "Téléconsultation médicale", desc: "Consultation individuelle sécurisée avec le médecin" },
  { key: "formation", icon: GraduationCap, label: "Formation en ligne", desc: "Session de groupe animée par le formateur" },
  { key: "expertise", icon: Users, label: "Réunion d'expertise", desc: "Conseil professionnel en petit comité" },
  { key: "webinaire", icon: Radio, label: "Webinaire public", desc: "Conférence et présentation académique" },
];

export default function Landing() {
  const [featured, setFeatured] = useState([]);
  const [services, setServices] = useState([]);
  const [devProducts, setDevProducts] = useState([]);
  const [liveWebinar, setLiveWebinar] = useState(null);
  const { scrollY } = useScroll();
  const yHero = useTransform(scrollY, [0, 500], [0, 60]);

  useEffect(() => {
    api.get("/products").then(({ data }) => {
      setFeatured(data.slice(0, 4));
      setServices(data.filter((p) => p.category === "Prestations de service").slice(0, 4));
      setDevProducts(data.filter((p) => p.category === "Développement informatique").slice(0, 4));
    }).catch(() => {});
    const pullLive = () => api.get("/webinars/live-now").then(({ data }) => setLiveWebinar(data.live ? data : null)).catch(() => {});
    pullLive();
    const iv = setInterval(pullLive, 20000);
    return () => clearInterval(iv);
  }, []);

  return (
    <div data-testid="landing-page">
      {liveWebinar && (liveWebinar.kind === "visio" ? (
        <div data-testid="landing-live-banner"
          className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-red-600 px-4 py-2.5 text-center text-sm font-semibold text-white">
          <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 animate-pulse rounded-full bg-white" /> EN DIRECT</span>
          <span className="font-normal">Une session <b>{liveWebinar.title}</b> est en cours.</span>
          <Link to="/prendre-rendez-vous" className="inline-flex items-center gap-1 underline underline-offset-2">Nos sessions <ArrowRight size={14} /></Link>
        </div>
      ) : (
        <Link to={`/webinaire/${liveWebinar.id}`} data-testid="landing-live-banner"
          className="group flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-red-600 px-4 py-2.5 text-center text-sm font-semibold text-white transition-colors hover:bg-red-700">
          <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 animate-pulse rounded-full bg-white" /> EN DIRECT</span>
          <span className="font-normal">Un webinaire est en cours : <b>{liveWebinar.title}</b></span>
          <span className="inline-flex items-center gap-1 rounded-full bg-white/20 px-2 py-0.5 text-[11px]"><Users size={12} /> {liveWebinar.viewers} en ligne</span>
          <span className="inline-flex items-center gap-1 underline underline-offset-2">Rejoindre <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" /></span>
        </Link>
      ))}
      {/* HERO */}
      <section className="relative overflow-hidden bg-navy text-white">
        <div className="pointer-events-none absolute inset-0 opacity-20" style={{
          backgroundImage: "radial-gradient(circle at 80% 20%, rgba(2,132,199,0.5), transparent 50%)",
        }} />
        <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-12 px-6 py-24 md:grid-cols-2 md:py-32">
          <div>
            <motion.p
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
              className="mb-6 inline-block rounded-full border border-sky-400/40 bg-sky-400/10 px-4 py-1.5 font-mono text-xs uppercase tracking-widest text-sky-300"
            >
              Santé · Informatique · Afrique francophone
            </motion.p>
            <h1 className="font-display text-4xl font-bold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              <MaskedLine delay={0.1}>Le savoir médical</MaskedLine>
              <MaskedLine delay={0.25}>et les outils de santé,</MaskedLine>
              <MaskedLine delay={0.4} className="text-sky-400">accessibles en un clic.</MaskedLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.7, ease: EASE }}
              className="mt-6 max-w-lg text-base leading-relaxed text-slate-300 md:text-lg"
            >
              Formations médicales, outils de gestion de l'information et applications métier —
              payez par Mobile Money ou carte bancaire et accédez à vos contenus instantanément.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.7, ease: EASE }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <Link to="/catalogue" data-testid="hero-cta-catalogue"
                className="flex items-center gap-2 rounded-full bg-sky-500 px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-sky-400">
                Explorer le catalogue <ArrowRight size={16} />
              </Link>
              <Link to="/offres" data-testid="hero-cta-offres"
                className="flex items-center gap-2 rounded-full bg-emerald-500 px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-emerald-400">
                Acheter des unités de temps <ArrowRight size={16} />
              </Link>
              <Link to="/auth?tab=register" data-testid="hero-cta-account"
                className="rounded-full border border-white/25 px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-white/10">
                Créer un compte
              </Link>
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/plaquette.pdf`} download data-testid="hero-plaquette-pdf"
                className="flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-white/20">
                <FileDown size={16} /> Télécharger la plaquette en PDF
              </a>
            </motion.div>
          </div>
          <motion.div
            style={{ y: yHero }}
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.35, duration: 0.9, ease: EASE }}
            className="relative"
          >
            <div className="overflow-hidden rounded-2xl border border-white/10 shadow-2xl">
              <img src={HERO_IMG} alt="Professionnelle de santé" className="max-h-[420px] w-full object-cover md:max-h-none" />
            </div>
            <div className="absolute -bottom-5 -left-5 rounded-xl border border-slate-200 bg-white p-4 text-navy shadow-xl">
              <p className="font-display text-2xl font-bold">48 h</p>
              <p className="text-xs text-slate-500">de téléchargement sécurisé, renouvelable</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* VIDÉO DE PRÉSENTATION */}
      <div className="bg-[#E0F2FE] pb-12">
        <PresentationVideo id="presentation-accueil" testid="home-presentation-video" />
        <div className="mx-auto max-w-5xl px-4 pt-6 sm:px-6">
          <Link to="/videos-whatsapp" data-testid="home-videos-whatsapp-btn"
            className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-sm font-semibold text-white shadow-sm transition-transform duration-200 hover:scale-[1.03]">
            <Video size={16} /> Toutes nos vidéos à partager sur WhatsApp <ArrowRight size={15} />
          </Link>
        </div>
      </div>

      {/* PLAGES PUBLICITAIRES — bandeau carrousel sous le héros */}
      <AdSlots title="À la une" eyebrow="En ce moment" limit={12} carousel />

      {/* CATEGORIES */}
      <section id="categories" className="mx-auto max-w-7xl px-6 py-24">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-widest text-ciel">Catalogue</p>
          <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-navy md:text-4xl">Quatre univers, une seule boutique</h2>
        </Reveal>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {CATS.map((c, i) => (
            <Reveal key={c.name} delay={i * 0.08}>
              <Link
                to={`/catalogue?cat=${encodeURIComponent(c.name)}`}
                data-testid={`category-card-${i}`}
                className="group block overflow-hidden rounded-xl border border-slate-200 bg-white transition-shadow duration-300 hover:shadow-lg"
              >
                <div className="relative aspect-[16/9] overflow-hidden">
                  <img src={c.img} alt={c.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/70 to-transparent" />
                  <c.icon className="absolute bottom-3 left-3 text-white" size={22} />
                </div>
                <div className="p-5">
                  <h3 className="font-display text-sm font-semibold leading-snug text-navy">{c.name}</h3>
                  <p className="mt-1.5 text-xs text-slate-500">{c.desc}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PRODUITS EN VEDETTE */}
      <section className="border-y border-slate-200 bg-white py-24">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="font-mono text-xs uppercase tracking-widest text-ciel">Sélection</p>
              <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-navy md:text-4xl">Produits en vedette</h2>
            </div>
            <Link to="/catalogue" data-testid="featured-see-all" className="flex items-center gap-2 text-sm font-semibold text-ciel transition-colors duration-200 hover:text-navy">
              Tout voir <ArrowRight size={15} />
            </Link>
          </Reveal>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {featured.map((p) => <ProductCard key={p.product_id} product={p} />)}
          </div>
        </div>
      </section>

      {/* OFFRE CABINET MÉDICAL */}
      <section className="border-b border-slate-200 bg-[#E0F2FE] py-20" data-testid="home-cabinet-medical">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-6 lg:grid-cols-2">
          <Reveal>
            <span className="inline-flex items-center gap-2 rounded-full border border-[#7DC4E8] bg-white px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-[#0369A1]">Nouveau · Solutions Vitalis</span>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-[#0C4A6E] md:text-4xl">Vitalis Cabinet Médical</h2>
            <p className="mt-4 max-w-xl text-base text-slate-700">La gestion numérique du cabinet solo ou du petit cabinet : admission, consultations, prescriptions et comptes rendus. 3 utilisateurs, tablette et support inclus.</p>
            <p className="mt-4 font-display text-2xl font-bold text-[#0369A1]">59 250 FCFA HT <span className="text-sm font-normal text-slate-600">/ mois · 69 915 FCFA TTC</span></p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/offres/cabinet-medical" data-testid="home-cabinet-cta" className="rounded-full bg-[#0369A1] px-6 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#075985]">Demander une démo</Link>
              <Link to="/nos-offres#guide" data-testid="home-offres-link" className="rounded-full border border-[#7DC4E8] bg-white px-6 py-3 text-sm font-semibold text-[#0369A1] transition-colors duration-200 hover:bg-[#E0F2FE]">Trouver mon offre en 3 clics</Link>
              <Link to="/solutions/comparatif" data-testid="home-comparatif-link" className="rounded-full border border-[#7DC4E8] bg-white px-6 py-3 text-sm font-semibold text-[#0369A1] transition-colors duration-200 hover:bg-[#E0F2FE]">Comparer les solutions</Link>
            </div>
            <div className="mt-6 rounded-2xl border border-[#7DC4E8] bg-white/80 p-4" data-testid="home-plaquette-email">
              <p className="mb-2 text-xs font-semibold text-navy">Recevoir la plaquette commerciale par email</p>
              <PlaquetteEmailForm source="accueil" compact />
            </div>
          </Reveal>
          <Reveal>
            <Link to="/offres/cabinet-medical" className="block overflow-hidden rounded-2xl border border-[#7DC4E8] shadow-sm">
              <img src="/images/cabinet-medical.jpg" alt="Vitalis Cabinet Médical" className="w-full transition-transform duration-500 hover:scale-[1.02]" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* PRESTATIONS DE SERVICE */}
      {services.length > 0 && (
        <section className="border-b border-slate-200 bg-white py-20">
          <div className="mx-auto max-w-7xl px-6">
            <Reveal className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="font-mono text-xs uppercase tracking-widest text-ciel">Sur rendez-vous</p>
                <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-navy md:text-4xl">Prestations de service</h2>
              </div>
              <Link to="/catalogue?cat=Prestations%20de%20service" data-testid="services-see-all" className="flex items-center gap-2 text-sm font-semibold text-ciel transition-colors duration-200 hover:text-navy">
                Tout voir <ArrowRight size={15} />
              </Link>
            </Reveal>
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {services.map((p) => <ProductCard key={p.product_id} product={p} />)}
            </div>
          </div>
        </section>
      )}

      {/* CONSULTATIONS & SESSIONS EN LIGNE */}
      <section id="consultations" className="border-b border-slate-200 bg-navy py-20">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-widest text-ciel">Visioconférence sécurisée</p>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-white md:text-4xl">Consultations & sessions en ligne</h2>
            <p className="mt-3 max-w-2xl text-sm text-slate-300">Choisissez le type de session et réservez un créneau en quelques secondes — Cabinet CARIS Santé. Lien de connexion et rappels automatiques par email, WhatsApp et SMS.</p>
          </Reveal>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {RDV_TYPES.map((t, i) => (
              <Reveal key={t.key} delay={i * 0.08}>
                <Link to={`/prendre-rendez-vous?type=${t.key}`} data-testid={`home-rdv-${t.key}`}
                  className="group flex h-full flex-col rounded-2xl border border-white/10 bg-white/5 p-6 transition-all duration-200 hover:border-ciel hover:bg-white/10">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-ciel/15 text-ciel"><t.icon size={22} /></span>
                  <p className="mt-4 font-display text-lg font-semibold text-white">{t.label}</p>
                  <p className="mt-1 flex-1 text-sm text-slate-300">{t.desc}</p>
                  <span className="mt-4 flex items-center gap-2 text-sm font-semibold text-ciel">
                    <CalendarCheck size={15} /> Réserver un créneau
                    <ArrowRight size={15} className="transition-transform duration-200 group-hover:translate-x-1" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.3}>
            <Link to="/prendre-rendez-vous" data-testid="home-rdv-all"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-ciel px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-sky-600">
              Voir tous les types de rendez-vous <ArrowRight size={15} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* DÉVELOPPEMENT INFORMATIQUE */}
      {devProducts.length > 0 && (
        <section className="border-b border-slate-200 bg-slate-50 py-20">
          <div className="mx-auto max-w-7xl px-6">
            <Reveal className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <p className="font-mono text-xs uppercase tracking-widest text-ciel">Sur mesure</p>
                <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-navy md:text-4xl">Développement informatique</h2>
              </div>
              <Link to="/catalogue?cat=D%C3%A9veloppement%20informatique" data-testid="dev-see-all" className="flex items-center gap-2 text-sm font-semibold text-ciel transition-colors duration-200 hover:text-navy">
                Tout voir <ArrowRight size={15} />
              </Link>
            </Reveal>
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {devProducts.map((p) => <ProductCard key={p.product_id} product={p} />)}
            </div>
          </div>
        </section>
      )}

      {/* ENGAGEMENTS */}
      <section id="engagements" className="mx-auto max-w-7xl px-6 py-24">
        <Reveal>
          <h2 className="font-display text-3xl font-bold tracking-tight text-navy md:text-4xl">Nos engagements</h2>
        </Reveal>
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {[
            { icon: Zap, title: "Accès immédiat", desc: "Dès la confirmation du paiement, vos fichiers et vidéos sont disponibles dans votre espace personnel, 24h/24." },
            { icon: ShieldCheck, title: "Paiement sécurisé", desc: "Mobile Money (Orange, MTN, Wave) et carte bancaire. Liens de téléchargement uniques et limités dans le temps." },
            { icon: Headset, title: "Garantie 7 jours", desc: "Un problème ? Soumettez une demande de remboursement motivée dans les 7 jours, traitée par le vendeur." },
          ].map((f, i) => (
            <Reveal key={f.title} delay={i * 0.1}>
              <div className="h-full rounded-xl border border-slate-200 bg-white p-8">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-sky-50 text-ciel">
                  <f.icon size={22} />
                </span>
                <h3 className="mt-5 font-display text-lg font-semibold text-navy">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-500">{f.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PLAGES PUBLICITAIRES — section dédiée « Prochaines sessions » */}
      <AdSlots title="Prochaines sessions & offres" eyebrow="Ne manquez rien" limit={3} dark />

      {/* CTA FINAL */}
      <section className="bg-navy py-20 text-white">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-8 px-6">
          <Reveal>
            <h2 className="font-display text-2xl font-bold md:text-3xl">Prêt à vous équiper ?</h2>
            <p className="mt-2 max-w-md text-slate-300">Créez votre compte en une minute et profitez du code <span className="font-mono font-semibold text-sky-400">VITALIS10</span> : -10 % sur votre première commande.</p>
          </Reveal>
          <Link to="/auth" data-testid="final-cta" className="rounded-full bg-sky-500 px-8 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-sky-400">
            Commencer maintenant
          </Link>
        </div>
      </section>
    </div>
  );
}
